import { parseNumber } from '../../../../calculator/util/parse-number'

export interface ProductCoatsInput {
  coats?: number
}

export interface ResolvedProductCoats {
  /** Resolved coats per product, same order as input. */
  resolved: number[]
  /** Distinct resolved values, ascending. */
  distinct: number[]
  /** True when the products disagree on coats. */
  isMixed: boolean
}

/**
 * Resolve each product's effective coat count, following the same `product.coats ?? coats`
 * formula the calculator uses (see productVolume in calculator/area-substrates), so description
 * text can match what was actually calculated instead of always reading the substrate's coats.
 * Uses parseNumber (not a bare `Number()` cast) so a malformed `coats` string falls back to 0
 * instead of silently producing NaN and marking every product "mixed".
 */
export function resolveProductCoats(
  substrateCoats: number | string,
  products: ProductCoatsInput[] = []
): ResolvedProductCoats {
  const resolved = products.map((product) => parseNumber(product.coats ?? substrateCoats))
  const distinct = Array.from(new Set(resolved)).sort((a, b) => a - b)
  return { resolved, distinct, isMixed: distinct.length > 1 }
}

/** `"1 Coat"` / `"3 Coats"` — the singular/plural text shared by every coats description builder. */
export function formatCoatCount(coats: number): string {
  return `${coats} Coat${coats === 1 ? '' : 's'}`
}

/**
 * Resolve the coats value to display for a line item: the distinct values joined (e.g. "1, 3")
 * when products disagree, the single resolved value when they agree, or the substrate coats
 * when there are no products to resolve.
 */
export function resolveDisplayCoats(
  substrateCoats: number | string,
  products: ProductCoatsInput[] = []
): number | string {
  const { distinct, isMixed } = resolveProductCoats(substrateCoats, products)
  return isMixed ? distinct.join(', ') : distinct.length === 1 ? distinct[0] : substrateCoats
}
